import { AbstractViewModel, type Draft } from '../utils/viewmodels/viewmodel';
import { useViewModel } from '../utils/viewmodels/useViewModel';
import type { ChatComponentPropsMap, ChatMessage, ChatMessageContent, ChatRole } from './chatbot.types';
import type { ChatbotState } from './chatbot.state';
import type { ChatbotStorageAdapter } from './chatbot.storage';

export class ChatbotViewModel extends AbstractViewModel<ChatbotState> {
  static initialState: ChatbotState = {
    messages: [],
    isInputDisabled: false,
    isAssistantThinking: false,
  };

  private storage: ChatbotStorageAdapter | null = null;

  /** Callback invoked when the user sends a message. Set by the host app. */
  private onUserMessage: ((text: string) => void) | null = null;

  /** Callback invoked when the user confirms a multiple-select. Set by the flow engine. */
  private onMultipleSelectConfirm: ((messageId: string, optionIds: string[]) => void) | null = null;

  constructor(storage?: ChatbotStorageAdapter) {
    super(ChatbotViewModel.initialState);

    if (storage) {
      this.storage = storage;
    }

    // Bind public methods used as callbacks
    this.sendMessage = this.sendMessage.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.selectOptions = this.selectOptions.bind(this);

    // Restore persisted state if available
    this.restore();
  }

  /**
   * Override to auto-persist after every state change.
   */
  override update(recipe: (state: Draft<ChatbotState>) => void) {
    super.update(recipe);
    this.persist();
  }

  // ── Storage ─────────────────────────────────────────────────────────────

  /** Attach or replace the storage adapter at runtime. */
  setStorage(storage: ChatbotStorageAdapter | null) {
    this.storage = storage;
  }

  // ── Callback wiring ────────────────────────────────────────────────────

  /** Set a callback that fires when the user sends a free-text message. */
  setOnUserMessage(callback: ((text: string) => void) | null) {
    this.onUserMessage = callback;
  }

  /** Set a callback that fires when the user confirms a multiple-select. */
  setOnMultipleSelectConfirm(callback: ((messageId: string, optionIds: string[]) => void) | null) {
    this.onMultipleSelectConfirm = callback;
  }

  // ── User actions ────────────────────────────────────────────────────────

  /** User sends a free-text message via the input field. */
  sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;

    this.addMessage('user', { type: 'text', text: trimmed });
    this.onUserMessage?.(trimmed);
  }

  /** User selects an option from a single-select message. */
  selectOption(messageId: string, optionId: string) {
    this.update((state) => {
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message && message.content.type === 'single-select') {
        message.content.selectedId = optionId;
      }
    });

    const { messages } = this.state.getValue();
    const msg = messages.find((message) => message.id === messageId);
    if (msg && msg.content.type === 'single-select') {
      const option = msg.content.options.find((option) => option.id === optionId);
      if (option) {
        this.addUserMessage({ type: 'text', text: option.label });
      }
    }
  }

  /** User confirms selections from a multiple-select message. */
  selectOptions(messageId: string, optionIds: string[]) {
    this.update((state) => {
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message && message.content.type === 'multiple-select') {
        message.content.selectedIds = optionIds;
      }
    });

    const { messages } = this.state.getValue();
    const msg = messages.find((message) => message.id === messageId);
    if (msg && msg.content.type === 'multiple-select') {
      const labels = msg.content.options
        .filter((option) => optionIds.includes(option.id))
        .map((option) => option.label);
      if (labels.length > 0) {
        this.addUserMessage({ type: 'text', text: labels.join(', ') });
      }
    }

    // Notify the flow engine (if subscribed) so it can advance the flow.
    this.onMultipleSelectConfirm?.(messageId, optionIds);
  }

  // ── Message helpers ───────────────────────────────────────────────────

  /** Add a controlled user message (e.g. reflecting a choice as if the user typed it). */
  addUserMessage(content: ChatMessageContent) {
    this.addMessage('user', content);
  }

  /** Add an assistant response message. */
  addAssistantMessage(content: ChatMessageContent) {
    this.addMessage('assistant', content);
  }

  /** Add a message from any role. */
  addMessage(role: ChatRole, content: ChatMessageContent) {
    const message: ChatMessage = {
      id: crypto.randomUUID(),
      role,
      content,
      createdAt: new Date().toISOString(),
    };

    this.update((state) => {
      if (role === 'assistant' && content.type !== 'thinking') {
        state.messages = state.messages.filter((msg) => msg.content.type !== 'thinking');
      }
      state.messages.push(message);
    });
  }

  // ── Thinking indicator ────────────────────────────────────────────────

  /** Show or hide the assistant "thinking" indicator. */
  setThinking(value: boolean, messages?: string[]) {
    if (value) {
      this.addMessage('assistant', { type: 'thinking', messages });
    } else {
      this.update((state) => {
        state.messages = state.messages.filter((msg) => msg.content.type !== 'thinking');
      });
    }
    this.update((state) => {
      state.isAssistantThinking = value;
    });
  }

  // ── Input control ─────────────────────────────────────────────────────

  enableInput() {
    this.update((state) => {
      state.isInputDisabled = false;
    });
  }

  disableInput() {
    this.update((state) => {
      state.isInputDisabled = true;
    });
  }

  // ── Component prop updates ────────────────────────────────────────

  /** Merge new props into a component-type message. */
  updateComponentProps(messageId: string, newProps: ChatComponentPropsMap) {
    this.update((state) => {
      const message = state.messages.find((msg) => msg.id === messageId);
      if (message && message.content.type === 'component') {
        message.content.props = { ...message.content.props, ...newProps };
      }
    });
  }

  /**
   * Disable all action-type messages (previous flow actions).
   * If `selectedActionId` is provided, marks it on the most recent
   * action message so the selected button is visually highlighted.
   */
  disablePreviousActions(selectedActionId?: string) {
    this.update((state) => {
      let lastActionMessage: (typeof state.messages)[number] | null = null;
      for (const message of state.messages) {
        if (message.content.type === 'actions') {
          message.content.disabled = true;
          lastActionMessage = message;
        }
      }
      // Mark the selected action on the most recent action message
      if (selectedActionId && lastActionMessage && lastActionMessage.content.type === 'actions') {
        lastActionMessage.content.selectedActionId = selectedActionId;
      }
    });
  }

  // ── Undo helpers ─────────────────────────────────────────────────────

  /**
   * Set or clear the `canRevert` flag on the last user message.
   * Used by the host app (via syncFlowState) to keep the flag correct after
   * page reload or any state change.
   */
  setCanRevert(enable: boolean) {
    this.update((state) => {
      // Clear canRevert on all messages first
      for (const message of state.messages) {
        message.canRevert = false;
      }
      // Mark the last non-removing user message if enabled
      if (enable) {
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const msg = state.messages[i];
          if (msg.role === 'user' && !msg.removing) {
            msg.canRevert = true;
            break;
          }
        }
      }
    });
  }

  /** Duration (ms) for the revert fade-out animation. */
  private static readonly REVERT_ANIMATION_MS = 350;

  /** Remove the last message (e.g. user echo on undo). */
  removeLastMessage() {
    this.update((state) => {
      state.messages.pop();
    });
  }

  /**
   * Mark the last N messages as "removing" (fade-out), then actually
   * remove them after the animation completes and re-enable the previous action.
   */
  revertMessages(count: number) {
    // 1. Mark messages as removing (triggers CSS fade-out)
    this.update((state) => {
      for (let i = 0; i < count && state.messages.length > 0; i++) {
        const msg = state.messages[state.messages.length - 1 - i];
        if (msg) msg.removing = true;
      }
    });

    // 2. After animation, actually remove them and re-enable the previous step's message
    setTimeout(() => {
      this.update((state) => {
        for (let i = 0; i < count && state.messages.length > 0; i++) {
          state.messages.pop();
        }
        // Re-enable the last disabled action OR clear a multiple-select's answered state
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const msg = state.messages[i];
          if (msg.content.type === 'actions' && msg.content.disabled) {
            msg.content.disabled = false;
            msg.content.selectedActionId = undefined;
            break;
          }
          if (msg.content.type === 'multiple-select' && msg.content.selectedIds) {
            msg.content.selectedIds = undefined;
            break;
          }
        }
      });
    }, ChatbotViewModel.REVERT_ANIMATION_MS);
  }

  /** Re-enable the last disabled action message (undo a selection). */
  revertLastAction() {
    this.update((state) => {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const msg = state.messages[i];
        if (msg.content.type === 'actions' && msg.content.disabled) {
          msg.content.disabled = false;
          msg.content.selectedActionId = undefined;
          break;
        }
      }
    });
  }

  // ── Reset ──────────────────────────────────────────────────────────────

  /** Clear all messages and storage. Returns to a blank slate. */
  reset() {
    this.storage?.clear();
    super.update((state) => {
      state.messages = [];
      state.isInputDisabled = false;
      state.isAssistantThinking = false;
    });
  }

  // ── Persistence ───────────────────────────────────────────────────────

  private persist() {
    if (!this.storage) return;
    const { messages } = this.state.getValue();
    this.storage.save({ messages });
  }

  private restore() {
    if (!this.storage) return;
    const snapshot = this.storage.load();
    if (!snapshot) return;

    super.update((state) => {
      // Filter out transient thinking messages — they shouldn't survive a page reload.
      state.messages = snapshot.messages.filter((message) => message.content.type !== 'thinking');
    });
  }
}

export const useChatbotViewModel = (storage?: ChatbotStorageAdapter) =>
  useViewModel<ChatbotState, ChatbotViewModel>(() => new ChatbotViewModel(storage));
